/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';

import IntroSection from '../../common/IntroSection';
import { useFormControls } from '../../../common/hooks/useFormControls';
import PressReleaseService from '../services/pressReleaseService';
import { checkoutURL } from '../helpers/divewireConstants';
import { pressReleaseContactFields as formControls } from '../../helpers/pressReleaseConstants';
import displayBackendErrors from '../../../common/helpers/displayBackendErrors';
import PaymentIntentService from '../services/paymentIntentService';
import LoadingScreen from '../../../common/LoadingScreen';
import EventsHelper from '../helpers/eventsHelper';
import { scrollToTopOrError } from '../../../common/helpers/scrollTo';
import ContactForm from '../components/ContactForm';

const ContactScreen = () => {
  const {
    pressRelease,
    dispatch,
    pressReleaseMetadata,
    nonFieldError,
    setNonFieldError,
    promoteSites,
  } = useOutletContext();
  const { token } = useParams();
  const navigate = useNavigate();
  const {
    fields,
    getFormValues,
    setField,
    updateForm,
    validateField,
    validateForm,
    setError,
  } = useFormControls(formControls);

  const [apiToken, setApiToken] = useState(token ?? null);
  const pressReleaseApi = new PressReleaseService(apiToken);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const eventsHelper = new EventsHelper(pressRelease, pressReleaseMetadata, promoteSites.sitesList);

  const createOrUpdatePaymentIntent = async (currentToken, createMode = false) => {
    const paymentIntentApi = new PaymentIntentService(currentToken);
    let response;
    if (createMode) {
      response = await paymentIntentApi.createPaymentIntent();
    } else {
      response = await paymentIntentApi.updatePaymentIntent();
    }
    return response;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const isValid = validateForm();
    if (isValid) {
      let prData;
      let updatedApiToken;
      let paymentIntentResponse;
      const formValues = getFormValues();
      try {
        if (apiToken) {
          const response = await pressReleaseApi.updatePressRelease(formValues);
          prData = response.data;
          updatedApiToken = apiToken;
          paymentIntentResponse = await createOrUpdatePaymentIntent(updatedApiToken); // TODO: more explicit errors?
        } else {
          const response = await pressReleaseApi.createPressRelease(formValues);
          prData = response.data;
          updatedApiToken = response.data.press_release.checkout_token;
          setApiToken(updatedApiToken);
          // unset published date if it's also not in the local state
          if (!pressRelease.publish_date) {
            prData.press_release.publish_date = '';
          }
          paymentIntentResponse = await createOrUpdatePaymentIntent(updatedApiToken, true); // TODO: more explicit errors?
          eventsHelper.handleBeginCheckout();
        }
      } catch (apiError) {
        setIsSubmitting(false);
        displayBackendErrors(apiError, setError, setNonFieldError, formControls);
        scrollToTopOrError(Object.keys(apiError?.response?.data));
      }
      if (prData && paymentIntentResponse) {
        setIsSubmitting(false);
        dispatch({
          type: 'SET_PRESS_RELEASE',
          payload: prData.press_release,
        });
        setNonFieldError('');
        navigate(`${checkoutURL}/${updatedApiToken}/create`);
      }
    }
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (pressRelease) {
      updateForm(pressRelease);
    }
  }, [pressRelease]);

  return (
    <>
      <main id="skip-link-target">
        <div className="row">
          <div className="columns large-12">
            <IntroSection title="Post a press release">
              To post your press release to {pressReleaseMetadata.current_site_name}, fill out the form below. Our self-serve platform allows
              you to publish your news immediately, or plan for it to go live at a scheduled date.
            </IntroSection>
          </div>
        </div>
        <div className="row contact-container">
          <div className="large-7 columns contact-info-container">
            <ContactForm
              fields={fields}
              setField={setField}
              validateField={validateField}
              nonFieldError={nonFieldError}
              setNonFieldError={setNonFieldError}
              pressReleaseMetadata={pressReleaseMetadata}
            />
            <section className="button-container">
              <button id="form-submit-pr" type="submit" onClick={handleSubmit} className="button button--medium">
                {token ? 'Continue to edit' : 'Get started'}
              </button>
              {pressReleaseMetadata.press_release_bulk_package_checkout_enabled && (
                <a href="/press-release/purchase-packages" className="button button--soft">
                  Purchase packages
                </a>
              )}
            </section>
          </div>
          <aside className="columns large-5 contact-price-container">
            <div className="pr-sidebar primary-color-accent-border">
              <div className="pr-sidebar__price-wrapper">
                <span className="pr-sidebar__price-wrapper__price">${pressReleaseMetadata.press_release_list_price / 100}</span>
                <span className="pr-sidebar__price-wrapper__qualifier"> per press release</span>
              </div>
              <div className="pr-sidebar__header">Included with each campaign:</div>
              <div className="pr-sidebar__list-wrapper">
                <ul className="pr-sidebar__list">
                  <li className="pr-sidebar__bullets">Permanent hosting on the company announcement page with your logo and header</li>
                  <li className="pr-sidebar__bullets">Daily newsletter section placement</li>
                  <li className="pr-sidebar__bullets">Sidebar and article footer visibility for up to 21 days</li>
                  <li className="pr-sidebar__bullets">7-day and 21-day visibility report including page views and impressions</li>
                </ul>
              </div>
              <div className="pr-sidebar__faq">
                Already puchased a package? Craft your company announcement and enter the redemption code on the checkout page. Learn more about DiveWire Press Release{' '}
                <a className="analytics t-pr-contact-faq" href={pressReleaseMetadata.press_release_faq_url}>here</a>
              </div>
            </div>
          </aside>
        </div>
      </main>
      {isSubmitting && <LoadingScreen isSubmitting={isSubmitting} />}
    </>
  );
};

export default ContactScreen;
